import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../../styles/UserMenuDrawer.css";
import logo from "../../assets/konteyner_logo.png";

import { useAuth } from "../../auth/AuthContext";
import { loginWithGoogle, logout } from "../../services/auth.service";

import UserMenuGuest from "../../auth/UserMenuGuest";
import UserMenuAccount from "../../auth/UserMenuAccount";

import { getMyAccountData } from "../../api/api";

const ANIM_MS = 450;

export default function UserMenuDrawer({ open, onClose, onOpenChat, chatId = "" }) {
  const navigate = useNavigate();
  const { t } = useTranslation("userMenu");
  const { user, loading } = useAuth();

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  const [purchases, setPurchases] = useState([]);
  const [chats, setChats] = useState([]);
  const [accLoading, setAccLoading] = useState(false);

  const [pendingRoute, setPendingRoute] = useState(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, ANIM_MS);

      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted && pendingRoute) {
      if (typeof pendingRoute === "string") {
        navigate(pendingRoute);
      } else {
        navigate(pendingRoute.path, { state: pendingRoute.state });
      }
      setPendingRoute(null);
    }
  }, [mounted, pendingRoute, navigate]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (mounted) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mounted, onClose]);

  useEffect(() => {
    let alive = true;

    async function loadAccount() {
      if (!user) {
        setPurchases([]);
        setChats([]);
        return;
      }

      setAccLoading(true);
      try {
        const data = await getMyAccountData();
        if (!alive) return;

        const all = Array.isArray(data?.purchases) ? data.purchases : [];

        const paidOnly = all
          .filter((p) => String(p.status || "").toLowerCase() === "paid")
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        setPurchases(paidOnly);

        const allChats = Array.isArray(data?.chats) ? data.chats : [];
        const sortedChats = [...allChats].sort((a, b) => {
          const ta = new Date(a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.updated_at || b.created_at || 0).getTime();
          return tb - ta;
        });
        setChats(sortedChats.slice(0, 10));
      } catch (e) {
        console.error("getMyAccountData error:", e);
        if (!alive) return;
        setPurchases([]);
        setChats([]);
      } finally {
        if (alive) setAccLoading(false);
      }
    }

    if (mounted && user && !loading) loadAccount();

    return () => {
      alive = false;
    };
  }, [user, loading, mounted]);

  useEffect(() => {
  async function reloadChatsAfterGuestSync() {
    setTimeout(async () => {
      try {
        const data = await getMyAccountData();

        const allChats = Array.isArray(data?.chats) ? data.chats : [];

        const sortedChats = [...allChats].sort((a, b) => {
          const ta = new Date(a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.updated_at || b.created_at || 0).getTime();
          return tb - ta;
        });

        setChats(sortedChats.slice(0, 10));
      } catch (e) {
        console.error("reload chats after guest sync error:", e);
      }
    }, 500);
  }

  window.addEventListener(
    "chatshop:chats-updated",
    reloadChatsAfterGuestSync
  );

  return () => {
    window.removeEventListener(
      "chatshop:chats-updated",
      reloadChatsAfterGuestSync
    );
  };
}, []);

  async function handleLogin() {
    try {
      await loginWithGoogle();
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(t("login_error"));
    }
  }

  async function handleLogout() {
    try {
      await logout();
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(t("logout_error"));
    }
  }

  const displayName = user?.displayName || t("default_user");
  const email = user?.email || "";

  if (!mounted) return null;

  return (
    <div
      className={`umOverlay ${open && !closing ? "isOpen" : "isClosing"}`}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <aside className="umDrawer" onClick={(e) => e.stopPropagation()}>
        <header className="umHeader">
          <button className="umCloseBtn" onClick={onClose} aria-label={t("close")} title={t("close")}>
            <FiX className="umCloseIcon" />
          </button>

          <img src={logo} alt={t("logo_alt")} className="umLogo" />
          <div />
        </header>

        <div className="umContent">
          {loading ? (
            <div className="umSub">{t("loading")}</div>
          ) : user ? (
            accLoading ? (
              <div className="umSub">{t("loading_account")}</div>
            ) : (
              <UserMenuAccount
  displayName={displayName}
  email={email}
  purchases={purchases}
  chats={chats}
  onLogout={handleLogout}
  onOpenOrders={() => {
    setPendingRoute({
      path: "/orders",
      state: {
        fromChatId: chatId,
        reopenUserMenuOnBack: true,
      },
    });
    onClose?.();
  }}
  onOpenLastOrder={(orderId) => {
    setPendingRoute({
      path: `/orders/${encodeURIComponent(orderId)}`,
      state: {
        fromOrders: true,
        fromChatId: chatId,
        reopenUserMenuOnBack: true,
      },
    });
    onClose?.();
  }}
  onOpenChat={(id) => {
    try {
      onOpenChat?.(id);
    } finally {
      onClose?.();
    }
  }}
/>
            )
          ) : (
            <UserMenuGuest onLogin={handleLogin} />
          )}
        </div>
      </aside>
    </div>
  );
}